import type { RecipeResult } from '@/types/RecipeClientTypes';

type Props = {
  recipe: RecipeResult;
  currentLocale: string;
  config: {
    [locale: string]: {
      id: string;
      prefix?: string;
    };
  };
};

type FormattedData = {
  recipe_id: string; // unique identifier for the recipe
  name: string; // name of the recipe
  description: string; // short description of the recipe
  image: string; // URL to the image, ideally a high resolution image
  servings: number; // integer
  serving_type: string; // either "PORTION" or "WHOLE"
  ingredient_groups: {
    id: string; // id of the group, which shouldn't change between requests, so we can identify the group and compare if it has changed
    name: string | null; // name of the group
    order: number; // order of the group in the list of groups (array_index)
    ingredients: {
      id: string; // id of the ingredient, which shouldn't change between requests, so we can identify the ingredient and compare if it has changed
      order: number; // order of the ingredient in the list of ingredients (array_index)
      amount: string; // string
      unit: string; // abbreviated unit (e.g. "g", "ml", "Stk.") - we can handle pretty much any unit string here, but we recommend using the abbreviated form
      prefix: string | null; // prefix of the ingredient (e.g. "halbierte, entsteinte")
      name: string; // name of the ingredient (e.g. "Zwetschken")
      suffix: string | null; // suffix of the ingredient (e.g. "frisch")
      optional: boolean; // if the ingredient is optional to use or not
    }[];
  }[];
};

// Mapping of serving types from local to HappyCart format
// Currently only for DE and AT, because HappyCart is only available in these countries
const SERVING_TYPE_MAP: { [local: string]: any } = {
  de_DE: {
    Stück: 'WHOLE',
    Portion: 'PORTION',
  },
  de_AT: {
    Stück: 'WHOLE',
    Portion: 'PORTION',
  },
};

const HappyCartDataJson = ({ recipe, config, currentLocale }: Props) => {
  if (!config || !config[currentLocale]) {
    return null;
  }

  const prefix = config[currentLocale]?.prefix || 'dr-oetker';

  const formattedData: FormattedData = {
    recipe_id: `${prefix}-${recipe.Id}`,
    name: recipe.Title,
    description: recipe.ShortDescription,
    image: recipe.Images?.[0]?.Formats?.[0].Url,
    servings: recipe.FromServings,
    serving_type:
      SERVING_TYPE_MAP[currentLocale][recipe?.ServingUnit?.Unit?.LongTitleSingular] ||
      recipe?.ServingUnit?.Unit?.LongTitleSingular,
    ingredient_groups: recipe.IngredientsAndEquipmentBlocks.map((group, groupIndex) => ({
      id: group.Id,
      name: group.Title.toString(),
      order: groupIndex,
      ingredients: group.Ingredients.map((ingredient, ingredientIndex) => ({
        id: ingredient.Id,
        order: ingredientIndex,
        amount: ingredient.ToAmount?.toString() || ingredient.FromAmount?.toString(),
        unit: ingredient.ArticleUnit?.Unit?.LongTitleSingular,
        prefix: null,
        name: ingredient.ArticleSummaryText,
        suffix: null,
        optional: false,
      })),
    })),
  };

  return (
    <script
      key={`happycart-data-${recipe.Id}`}
      id="happycart-data"
      type="application/json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(formattedData),
      }}
    />
  );
};

export default HappyCartDataJson;
